import { useEffect, useMemo, useState } from "react";
import { useProfile } from "../state/ProfileContext";
import { place_offset_at, timezone_name_for } from "../lib/timezones";
import {
  list_session_names,
  reconcile_local_with_cloud,
  remember_session,
  can_list_full_roster,
  persist_session,
  type SessionName,
} from "../lib/session_sync";
import { geocode_with_display } from "../lib/geocoding";
import { save_state } from "../lib/json_store";
import { Card, Banner, useAsyncAction, Spinner } from "../ui/common";
import {
  parseISODate,
  parseISOTime,
  plainTime,
  plainDateTime,
  combine,
  addSeconds,
  diffSeconds,
  diffDays,
  dateOnly,
  tryParseISOTime,
  todayLocal,
  formatISODate,
  formatHM,
  timeOnly,
  type PlainTime,
  type PlainDate,
} from "../lib/datetime";

/** Offset hours → the raw input-box value ("-6", "5.5"). */
function fmtOffset(hours: number): string {
  return String(Number(hours));
}

/** Offset hours → a friendly label ("UTC−6", "UTC+5:30"). */
function displayOffset(hours: number): string {
  const sign = hours < 0 ? "−" : "+";
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}

export function Intake() {
  const { openUser, startNew } = useProfile();
  // Start with this browser's local registry (the people THIS device created). We ALSO merge in
  // everyone saved on DynamoDB when this session may see the full roster — i.e. running locally
  // (npm run dev), or a deployed origin opened with the `#<password>+all` unlock. A plain public
  // visitor must never see the whole roster.
  const showFullRoster = can_list_full_roster();
  const [people, setPeople] = useState<SessionName[]>(() => list_session_names());
  const [selected, setSelected] = useState<string>(people[0]?.slug ?? "");
  const [loadingCloud, setLoadingCloud] = useState(showFullRoster);
  const openAction = useAsyncAction();

  useEffect(() => {
    if (!showFullRoster) return; // public deployed origin: local registry only, no cloud scan
    let cancelled = false;
    // The cloud is the source of truth: this also FORGETS any locally-remembered person who no
    // longer exists on DynamoDB (e.g. deleted from the admin page), so the picker never offers a
    // stale entry. A null result (unreachable/error) leaves the local list untouched.
    reconcile_local_with_cloud()
      .then((cloud) => {
        if (cancelled) return;
        setLoadingCloud(false);
        if (cloud === null) return;
        // Remember them locally so they resume instantly (even offline) next time.
        for (const p of cloud) remember_session(p.slug, p.first_name);
        setPeople(cloud); // exactly the cloud roster — stale locals already pruned
      })
      .catch(() => {
        if (!cancelled) setLoadingCloud(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showFullRoster]);

  // Keep the picker valid: default to the first person, and reset if the selected person was pruned.
  useEffect(() => {
    if (people.length && !people.some((p) => p.slug === selected)) setSelected(people[0].slug);
  }, [people, selected]);

  const [firstName, setFirstName] = useState("");
  // Optional — a classical-rule input only (sex-determination parity check, traits.ts). Unset by
  // default; "other" / "prefer_not" are honoured as a no-op in scoring.
  const [gender, setGender] = useState("");
  const [place, setPlace] = useState("");
  const [resolved, setResolved] = useState<string | null>(null);
  const [lat, setLat] = useState("0");
  const [lon, setLon] = useState("0");
  const [birthDate, setBirthDate] = useState("");
  // Two ways to bound the search window:
  //  - "around": a best-guess time ± hours (symmetric — the classic "born around noon" case).
  //  - "after": a known START time and how long FORWARD to search, which may span several days —
  //    for induced/scheduled deliveries where the natural birth happened earlier than the planned
  //    time, so the true moment is somewhere AFTER a known earliest point.
  const [windowMode, setWindowMode] = useState<"around" | "after">("around");
  const [centerTime, setCenterTime] = useState("12:00");
  const [pmHours, setPmHours] = useState("12");
  const [startTime, setStartTime] = useState("00:00");
  const [afterDays, setAfterDays] = useState("1");
  const [afterHours, setAfterHours] = useState("0");
  const [formError, setFormError] = useState<string | null>(null);
  // User-confirmed UTC offset (hours, as a string). Surfaced ONLY for pre-1970 births, where the
  // IANA tzdb projects modern DST rules backward and is often an hour off the real birth clock.
  const [tzOverride, setTzOverride] = useState("");

  // Resolve what the tzdb thinks the offset is for this birth, plus the location's standard-time
  // (January) offset, so the user can confirm/correct it for unreliable historical dates.
  const tzInfo = useMemo(() => {
    const latN = Number(lat);
    const lonN = Number(lon);
    if ((latN === 0 && lonN === 0) || !birthDate) return null;
    let bd;
    try {
      bd = parseISODate(birthDate);
    } catch {
      return null;
    }
    const zone = timezone_name_for(latN, lonN);
    if (!zone) return null;
    // Noon of the birth date (matches resolve_timezone_offset), and mid-January for the standard
    // (non-DST) offset of the same location.
    const detected = place_offset_at(latN, lonN, plainDateTime(bd.year, bd.month, bd.day, 12, 0, 0), 0);
    const standard = place_offset_at(latN, lonN, plainDateTime(bd.year, 1, 15, 12, 0, 0), 0);
    return { zone, detected, standard, isHistorical: bd.year < 1970, hasDst: detected !== standard };
  }, [lat, lon, birthDate]);

  // Default the override box to the detected offset whenever the resolved timezone changes, so a
  // pre-1970 birth is PINNED by default (no within-day tzdb DST flip) and the user only edits if
  // they know the clock differed (e.g. it shows CDT but the certificate says CST).
  useEffect(() => {
    if (tzInfo?.isHistorical) setTzOverride(fmtOffset(tzInfo.detected));
    else setTzOverride("");
  }, [tzInfo?.zone, tzInfo?.detected, tzInfo?.isHistorical]);

  const geocodeAction = useAsyncAction();
  const startAction = useAsyncAction();

  // Resolve the search window for the active mode. Returns time-of-day bounds plus the END DATE
  // relative to the birth date (0 = same day, 1 = next day, … for multi-day forward searches).
  // SHARED by the live preview and `begin()` so they can never drift. `bd` is the start (birth) date.
  function resolveWindow(
    bd: PlainDate,
  ): { start: PlainTime; end: PlainTime; endDate: PlainDate; dayOffset: number } | { error: string } {
    if (windowMode === "after") {
      let start: PlainTime;
      try {
        start = parseISOTime(startTime);
      } catch (e: any) {
        return { error: String(e?.message ?? e) };
      }
      const days = Number(afterDays);
      const hours = Number(afterHours);
      if (!Number.isFinite(days) || !Number.isFinite(hours) || days < 0 || hours < 0) {
        return { error: "Forward duration must be zero or more days and hours." };
      }
      const totalSeconds = Math.round((days * 24 + hours) * 3600);
      if (totalSeconds <= 0) {
        return { error: "Forward duration must be greater than zero." };
      }
      const startDt = combine(bd, start);
      const endDt = addSeconds(startDt, totalSeconds);
      const endDate = dateOnly(endDt);
      return { start, end: timeOnly(endDt), endDate, dayOffset: diffDays(endDate, bd) };
    }
    // "around": centre ± hours, clamped to the birth day (single-day symmetric search).
    let center: PlainTime;
    try {
      center = parseISOTime(centerTime);
    } catch (e: any) {
      return { error: String(e?.message ?? e) };
    }
    const half = Number(pmHours) * 3600;
    if (!Number.isFinite(half) || half <= 0) return { error: "± hours must be greater than zero." };
    const dayStart = combine(bd, plainTime(0, 0, 0));
    const dayEnd = combine(bd, plainTime(23, 59, 59));
    const centerDt = combine(bd, center);
    const startDt =
      diffSeconds(addSeconds(centerDt, -half), dayStart) > 0 ? addSeconds(centerDt, -half) : dayStart;
    let endDt = addSeconds(centerDt, half);
    if (diffSeconds(endDt, startDt) >= 24 * 3600) endDt = dayEnd;
    // A late centre can roll the end past midnight — derive the real end date (like the "after"
    // branch) so the preview can warn about the overnight window instead of silently claiming day 0.
    const endDate = dateOnly(endDt);
    return { start: timeOnly(startDt), end: timeOnly(endDt), endDate, dayOffset: diffDays(endDate, bd) };
  }

  // Live preview of the window the inputs will produce — using the SAME formula as `begin()`.
  // Surfaces the AM/PM trap of the native time picker (e.g. "11:00" silently captured as 11 PM),
  // and shows the day-span for a multi-day forward search.
  const windowPreview = useMemo(() => {
    const base = { year: 2000, month: 1, day: 1 };
    const win = resolveWindow(base);
    if ("error" in win) return null;
    const anchor = windowMode === "after" ? tryParseISOTime(startTime) : tryParseISOTime(centerTime);
    const ampm = anchor && anchor.hour < 12 ? "AM" : "PM";
    const h12 = anchor ? (anchor.hour % 12 === 0 ? 12 : anchor.hour % 12) : 12;
    return {
      mode: windowMode,
      anchor12: anchor ? `${h12}:${String(anchor.minute).padStart(2, "0")} ${ampm}` : "—",
      start: formatHM(win.start),
      end: formatHM(win.end),
      dayOffset: win.dayOffset,
      overnight: win.dayOffset === 1,
      multiday: win.dayOffset >= 1,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowMode, centerTime, pmHours, startTime, afterDays, afterHours]);

  async function findCoords() {
    await geocodeAction.run("Looking up the place via OpenStreetMap…", async () => {
      const found = await geocode_with_display(place);
      if (found) {
        setLat(String(found.lat));
        setLon(String(found.lon));
        setResolved(found.display_name || place);
      } else {
        setResolved(null);
        geocodeAction.setError(
          "Couldn't resolve that place (offline, or the name is ambiguous). Add the country, or enter coordinates manually.",
        );
      }
    });
  }

  function begin() {
    setFormError(null);
    if (!firstName.trim()) return setFormError("First name is required.");
    const latN = Number(lat);
    const lonN = Number(lon);
    if (
      lat.trim() === "" ||
      lon.trim() === "" ||
      !Number.isFinite(latN) ||
      !Number.isFinite(lonN) ||
      (latN === 0 && lonN === 0)
    )
      return setFormError("Latitude and longitude are required for chart calculations.");
    if (Math.abs(latN) > 90 || Math.abs(lonN) > 180)
      return setFormError("Latitude must be between −90 and 90, and longitude between −180 and 180.");
    if (!birthDate) return setFormError("Date of birth is required.");

    let bd: PlainDate;
    try {
      bd = parseISODate(birthDate);
    } catch (e: any) {
      return setFormError(String(e?.message ?? e));
    }

    // Resolve the window for the active mode ("around" centre±hours, or "after" start + forward
    // duration which may span several days). range_end_date carries the (possibly later) end day.
    const win = resolveWindow(bd);
    if ("error" in win) return setFormError(win.error);
    const { start: startT, end: endT, endDate } = win;

    // Pin the user-confirmed offset only for pre-1970 births (where the tzdb is unreliable);
    // modern births keep the live per-moment tzdb resolution (handles DST-transition birthdays).
    let tz_offset_override: number | null = null;
    if (tzInfo?.isHistorical && tzOverride.trim() !== "") {
      const v = Number(tzOverride);
      if (!Number.isFinite(v) || v < -14 || v > 14) {
        return setFormError("UTC offset must be a number between −14 and +14.");
      }
      tz_offset_override = v;
    }

    startAction.run("Creating the profile…", async () => {
      const s = await startNew({
        first_name: firstName,
        birth_date: bd,
        lat: latN,
        lon: lonN,
        certainty: "range",
        entered_time: null,
        range_start: { hour: startT.hour, minute: startT.minute, second: startT.second },
        range_end: { hour: endT.hour, minute: endT.minute, second: endT.second },
        range_end_date: endDate,
        tz_offset_override,
      });
      // Optional gender (classical parity-check input only). startNew's arg shape is owned by the
      // session layer, so the field is stamped onto the freshly-created profile and re-persisted;
      // the in-memory session holds the same object, so the running state carries it too.
      if (gender) {
        s.user_profile["gender"] = gender;
        save_state(s.user_path, s.run_path, s.user_profile, s.run_data);
        await persist_session(String(s.user_profile["slug"])).catch(() => {});
      }
    });
  }

  function openSelected() {
    if (!selected) return;
    openAction.run("Loading from the cloud…", async () => {
      await openUser(selected);
    });
  }

  return (
    <main className="app-main">
      <h1>🔭 Birth Time Rectification</h1>
      <p className="caption">
        Narrow a birth time from dated, astrologically relevant life events — to a confidence
        window first, then to the exact second once you lock a rising sign and drill down.
      </p>

      {(people.length > 0 || loadingCloud) && (
        <Card>
          <h3>Resume a saved person</h3>
          {people.length === 0 && loadingCloud ? (
            <p className="caption">
              <Spinner /> Loading everyone saved in the cloud…
            </p>
          ) : (
            <>
              <div className="row" style={{ alignItems: "flex-end" }}>
                <label className="field" style={{ flex: 3, marginBottom: 0 }}>
                  <span>Saved people</span>
                  <select value={selected} onChange={(e) => setSelected(e.target.value)}>
                    {people.map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.first_name}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="primary" onClick={openSelected} disabled={openAction.pending}>
                  {openAction.pending ? <Spinner /> : null} Open
                </button>
              </div>
              {showFullRoster && (
                <p className="caption" style={{ marginTop: "0.4rem" }}>
                  {loadingCloud ? (
                    <>
                      <Spinner /> Syncing the full cloud list…
                    </>
                  ) : (
                    `${people.length} ${people.length === 1 ? "person" : "people"} on file (DynamoDB · full roster unlocked).`
                  )}
                </p>
              )}
            </>
          )}
          {openAction.error && <Banner kind="error">{openAction.error}</Banner>}
        </Card>
      )}

      <Card>
        <h3>Start someone new</h3>
        {formError && <Banner kind="error">{formError}</Banner>}

        <label className="field">
          <span>First name</span>
          <input value={firstName} maxLength={60} onChange={(e) => setFirstName(e.target.value)} />
        </label>

        <label className="field">
          <span>Gender (optional — used only for a classical chart parity check)</span>
          <select value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Not specified</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not">Prefer not to say</option>
          </select>
        </label>

        <label className="field">
          <span>Birth place (city, country)</span>
          <input
            value={place}
            placeholder="e.g. Mumbai, India"
            maxLength={120}
            onChange={(e) => setPlace(e.target.value)}
          />
        </label>
        <button onClick={findCoords} disabled={!place.trim() || geocodeAction.pending}>
          {geocodeAction.pending ? <Spinner /> : null} Find coordinates from place
        </button>
        {resolved && <p className="caption">📍 Using: {resolved}</p>}
        {geocodeAction.error && <Banner kind="warn">{geocodeAction.error}</Banner>}

        <div className="row" style={{ marginTop: "0.75rem" }}>
          <label className="field">
            <span>Latitude</span>
            <input value={lat} onChange={(e) => setLat(e.target.value)} inputMode="decimal" />
          </label>
          <label className="field">
            <span>Longitude</span>
            <input value={lon} onChange={(e) => setLon(e.target.value)} inputMode="decimal" />
          </label>
        </div>

        <p className="caption">
          Pick the birth date, then how to bound the search. No idea? Leave the defaults
          (<strong>12:00 ± 12h</strong>) — that searches the whole day. Your guess only sets the
          window; it never influences the result.
        </p>

        <label className="field">
          <span>Date of birth{windowMode === "after" ? " (earliest / start day)" : ""}</span>
          <input
            type="date"
            value={birthDate}
            min="1900-01-01"
            max={formatISODate(todayLocal())}
            onChange={(e) => setBirthDate(e.target.value)}
          />
        </label>
        {tzInfo && (
          <p className="caption">
            🕑 Timezone: {tzInfo.zone} ({displayOffset(tzInfo.detected)} on this date
            {tzInfo.hasDst ? ", daylight time" : ""}).
          </p>
        )}
        {tzInfo?.isHistorical && (
          <div style={{ marginTop: "0.25rem" }}>
            <Banner kind="warn">
              Historical timezone data before 1970 is unreliable — the database applies modern
              daylight-saving rules backward, which is often an hour off the real birth clock.{" "}
              <strong>Confirm the UTC offset</strong> that the clock showed at birth. This location's
              standard (non-daylight) offset is <strong>{displayOffset(tzInfo.standard)}</strong>
              {tzInfo.hasDst ? `; the detected value assumes daylight time (${displayOffset(tzInfo.detected)}).` : "."}{" "}
              If you're unsure and the place likely didn't observe daylight time then, use the
              standard offset.
            </Banner>
            <label className="field" style={{ marginTop: "0.5rem" }}>
              <span>UTC offset at birth (hours)</span>
              <input
                type="number"
                min={-14}
                max={14}
                step={0.25}
                value={tzOverride}
                onChange={(e) => setTzOverride(e.target.value)}
              />
            </label>
          </div>
        )}

        {/* How to bound the search: a symmetric ± window around a guess, or a forward window from a
            known start (which may span several days — induced/scheduled deliveries born early). */}
        <div className="seg-toggle" role="tablist" style={{ display: "flex", gap: 6, margin: "0.25rem 0 0.6rem" }}>
          <button
            type="button"
            role="tab"
            aria-selected={windowMode === "around"}
            className={windowMode === "around" ? "primary" : ""}
            style={{ flex: 1 }}
            onClick={() => setWindowMode("around")}
          >
            Around a time (±)
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={windowMode === "after"}
            className={windowMode === "after" ? "primary" : ""}
            style={{ flex: 1 }}
            onClick={() => setWindowMode("after")}
          >
            After a time (forward)
          </button>
        </div>

        {windowMode === "around" ? (
          <div className="row">
            <label className="field">
              <span>Approx birth time (centre of search)</span>
              <input type="time" value={centerTime} onChange={(e) => setCenterTime(e.target.value)} />
            </label>
            <label className="field">
              <span>± hours to search around it</span>
              <input
                type="number"
                min={0.5}
                max={12}
                step={0.5}
                value={pmHours}
                onChange={(e) => setPmHours(e.target.value)}
              />
            </label>
          </div>
        ) : (
          <>
            <p className="caption" style={{ marginTop: "-0.25rem" }}>
              For an <strong>induced or scheduled delivery</strong>: the natural birth may have come
              earlier than planned, so search <strong>forward</strong> from a known earliest time —
              across multiple days if needed.
            </p>
            <div className="row">
              <label className="field">
                <span>Start time (earliest)</span>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </label>
              <label className="field">
                <span>Days forward</span>
                <input
                  type="number"
                  min={0}
                  max={14}
                  step={1}
                  value={afterDays}
                  onChange={(e) => setAfterDays(e.target.value)}
                />
              </label>
              <label className="field">
                <span>+ hours</span>
                <input
                  type="number"
                  min={0}
                  max={23}
                  step={0.5}
                  value={afterHours}
                  onChange={(e) => setAfterHours(e.target.value)}
                />
              </label>
            </div>
          </>
        )}

        {windowPreview && (
          <p
            className="caption"
            style={{
              marginTop: "-0.35rem",
              color:
                windowPreview.overnight || windowPreview.multiday ? "var(--accent2)" : "var(--ink2)",
            }}
          >
            {windowPreview.mode === "around" ? "Centre " : "From "}
            <strong>{windowPreview.anchor12}</strong> → searches{" "}
            <strong>
              {windowPreview.start}–{windowPreview.end}
            </strong>
            {windowPreview.multiday && windowPreview.dayOffset >= 2 ? (
              <> over <strong>{windowPreview.dayOffset + 1} days</strong> (induced-delivery search).</>
            ) : windowPreview.overnight ? (
              windowPreview.mode === "around" ? (
                <> ⚠︎ crosses midnight into the next day — did you mean an AM time?</>
              ) : (
                <> into the next day.</>
              )
            ) : null}
          </p>
        )}

        {startAction.error && <Banner kind="error">{startAction.error}</Banner>}
        <button className="primary full" onClick={begin} disabled={startAction.pending}>
          {startAction.pending ? <Spinner /> : null} Begin rectification →
        </button>
      </Card>
    </main>
  );
}
